import {
  eCaseCommentMessageType,
  IAddSpecificCaseCommentRequest,
  ICreateTopicEscalationRequest,
  ILCHttpResponse,
  IOpenCaseRequest,
  IPaginationResponse,
  IRiskHRCase,
  IUpdateTopicEscalationRequest,
} from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import {
  IAddCollaboratorRequest,
  IGetCasesRequest,
  IGetSpecificCaseRequest,
  IRemoveCollaboratorRequest,
  IUpdateSpecificCaseAssigneeRequest,
  IUpdateSpecificCasePriorityRequest,
  IUpdateSpecificCaseStatusRequest,
} from '../../interfaces/cases.interface'
import { IMyDashboardSummary } from '../pages/myDashboard/myDashboard.helpers'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export const casesApi = createApi({
  reducerPath: 'casesApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/cases`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  tagTypes: ['Case'],
  endpoints: (builder) => ({
    getCases: builder.mutation<IPaginationResponse<IRiskHRCase[]>, IGetCasesRequest>({
      query: ({ pageIndex, pageSize, sortBy, search, locale }) => {
        return {
          url: 'pagination',
          method: 'POST',
          params: locale ? { locale } : undefined,
          body: {
            pageIndex,
            pageSize,
            sortBy,
            search,
          },
        }
      },
      transformResponse: (response: ILCHttpResponse<IPaginationResponse<IRiskHRCase[]>>) => {
        // Extract the pagination data from the ILCHttpResponse wrapper
        return response.data as IPaginationResponse<IRiskHRCase[]>
      },
    }),
    getMyDashboardSummary: builder.query<
      IMyDashboardSummary,
      { startDate?: string; endDate?: string } | undefined
    >({
      query: (params) => ({
        url: 'my-summary',
        method: 'GET',
        params: params ?? {},
      }),
      transformResponse: (response: ILCHttpResponse<IMyDashboardSummary>) => response.data as IMyDashboardSummary,
      providesTags: ['Case'],
    }),
    getSpecificCase: builder.query<IRiskHRCase, IGetSpecificCaseRequest>({
      query: ({ caseId }) => {
        return {
          url: `${caseId}`,
          method: 'GET',
        }
      },
      providesTags: ['Case'],
    }),
    updateSpecificCaseStatus: builder.mutation<IRiskHRCase, IUpdateSpecificCaseStatusRequest>({
      query: ({ caseId, status }) => {
        return {
          url: `${caseId}/status`,
          method: 'PUT',
          body: { status },
        }
      },
    }),
    updateSpecificCasePriority: builder.mutation<IRiskHRCase, IUpdateSpecificCasePriorityRequest>({
      query: ({ caseId, priority }) => {
        return {
          url: `${caseId}/priority`,
          method: 'PUT',
          body: { priority },
        }
      },
    }),
    updateSpecificCaseAssignee: builder.mutation<IRiskHRCase, IUpdateSpecificCaseAssigneeRequest>({
      query: ({ caseId, assignTo }) => {
        return {
          url: `${caseId}/assignee`,
          method: 'PUT',
          body: { assignTo },
        }
      },
    }),
    addCollaborator: builder.mutation<IRiskHRCase, IAddCollaboratorRequest>({
      query: (request: IAddCollaboratorRequest) => {
        const { caseId, collaboratorIdentity, topicId } = request
        return {
          url: `${caseId}/collaborator`,
          method: 'POST',
          body: { collaboratorIdentity, topicId },
        }
      },
    }),
    unassignCollaborator: builder.mutation<IRiskHRCase, IRemoveCollaboratorRequest>({
      query: (request: IRemoveCollaboratorRequest) => {
        const { caseId, topicId } = request
        return {
          url: `${caseId}/collaborator`,
          method: 'DELETE',
          body: { topicId },
        }
      },
    }),
    addSpecificCaseComment: builder.mutation<IRiskHRCase, IAddSpecificCaseCommentRequest>({
      query: (request: IAddSpecificCaseCommentRequest) => {
        if (request.type === eCaseCommentMessageType.QUESTION) {
          const { caseId, comment, type, topicId } = request

          return {
            url: `${caseId}/comment`,
            method: 'POST',
            body: { comment, type, topicId },
          }
        }

        const { caseId, comment, type } = request
        return {
          url: `${caseId}/comment`,
          method: 'POST',
          body: { comment, type },
        }
      },
      invalidatesTags: ['Case'],
    }),
    openCase: builder.mutation<IRiskHRCase, IOpenCaseRequest>({
      query: ({ title, employeeId, evaluationId }) => {
        return {
          url: 'open',
          method: 'POST',
          body: {
            title,
            employeeId,
            evaluationId,
          },
        }
      },
    }),
    archiveCases: builder.mutation<IRiskHRCase[], { caseIds: number[] }>({
      query: ({ caseIds }) => {
        return {
          url: 'archive',
          method: 'PUT',
          body: { caseIds },
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IRiskHRCase[]>, meta) => {
        return rawResult.data as IRiskHRCase[]
      },
    }),
    restoreCases: builder.mutation<IRiskHRCase[], { caseIds: number[] }>({
      query: ({ caseIds }) => {
        return {
          url: 'restore',
          method: 'PUT',
          body: { caseIds },
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IRiskHRCase[]>, meta) => {
        return rawResult.data as IRiskHRCase[]
      },
    }),
    deleteArchivedCases: builder.mutation<IRiskHRCase[], { caseIds: number[] }>({
      query: ({ caseIds }) => {
        return {
          url: 'archive/delete',
          method: 'DELETE',
          body: { caseIds },
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IRiskHRCase[]>, meta) => {
        return rawResult.data as IRiskHRCase[]
      },
    }),
    getArchivedCasesResults: builder.mutation<IPaginationResponse<any[]>, IGetCasesRequest>({
      query: ({ pageIndex, pageSize, sortBy, search, locale }) => {
        return {
          url: 'archived/get-pagination',
          method: 'POST',
          params: locale ? { locale } : undefined,
          body: {
            pageIndex,
            pageSize,
            sortBy,
            search,
          },
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IPaginationResponse<any[]>>, meta) => {
        return rawResult.data as IPaginationResponse<any[]>
      },
    }),
    createTopicEscalation: builder.mutation<IRiskHRCase, { caseId: number } & ICreateTopicEscalationRequest>({
      query: ({ caseId, ...body }) => ({
        url: `${caseId}/escalations`,
        method: 'POST',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IRiskHRCase>) => rawResult.data as IRiskHRCase,
      invalidatesTags: ['Case'],
    }),
    updateTopicEscalation: builder.mutation<IRiskHRCase, { caseId: number; topicId: string } & IUpdateTopicEscalationRequest>({
      query: ({ caseId, topicId, ...body }) => ({
        url: `${caseId}/escalations/${topicId}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IRiskHRCase>) => rawResult.data as IRiskHRCase,
      invalidatesTags: ['Case'],
    }),
    cancelTopicEscalation: builder.mutation<IRiskHRCase, { caseId: number; topicId: string }>({
      query: ({ caseId, topicId }) => ({
        url: `${caseId}/escalations/${topicId}`,
        method: 'DELETE',
      }),
      transformResponse: (rawResult: ILCHttpResponse<IRiskHRCase>) => rawResult.data as IRiskHRCase,
      invalidatesTags: ['Case'],
    }),
  }),
})

export const {
  useGetMyDashboardSummaryQuery,
  useGetCasesMutation,
  useLazyGetSpecificCaseQuery,
  useUpdateSpecificCaseStatusMutation,
  useUpdateSpecificCasePriorityMutation,
  useUpdateSpecificCaseAssigneeMutation,
  useAddCollaboratorMutation,
  useUnassignCollaboratorMutation,
  useAddSpecificCaseCommentMutation,
  useOpenCaseMutation,
  useArchiveCasesMutation,
  useRestoreCasesMutation,
  useDeleteArchivedCasesMutation,
  useGetArchivedCasesResultsMutation,
  useCreateTopicEscalationMutation,
  useUpdateTopicEscalationMutation,
  useCancelTopicEscalationMutation,
} = casesApi
